<template>
  <div
    class="m-0 py-0 columns is-mobile is-vcentered row-file"
    :class="{ 'container-attached-doc': initialDoc }"
  >
    <b-loading data-cy="LOADING" :isFullPage="false" :active="isLoading" />
    <div class="column my-5 px-0">
      <div
        class="columns is-vcentered no-margin-x"
        :class="isEditable ? 'ml-10' : 'ml-5'"
        data-cy="file-attachment"
      >
        <span v-if="initialDoc" class="column is-6 has-text-left py-0">
          {{ file.attachment_name }}
        </span>

        <b-field
          v-if="!initialDoc"
          class="column is-full-mobile custom-tablet-mb py-0"
          :class="isArchived ? `is-6` : `is-two-fifths`"
        >
          <b-input
            v-model="fileName"
            :value="file.attachment_name"
            :placeholder="General.PLACEHOLDER.DOCUMENT_NAME"
            data-cy="name-input"
            :disabled="
              file.document_json ||
                file.is_doe_document_model ||
                file.to_publish ||
                hasSignature ||
                file.non_mutable
            "
            :class="{ 'same-line-counter': showInputCounter }"
            :aria-label="`${General.DOCUMENT_NAME}: ${file.attachment_name}`"
            minlength="1"
            :hasCounter="showInputCounter"
            v-bind="isEditable ? { maxlength: 70 } : { maxlength: 100 }"
            @blur="checkFieldName"
          />
        </b-field>

        <b-field
          v-if="!initialDoc"
          class="column is-full-mobile custom-tablet-mb py-0"
          :class="isArchived ? `is-3` : `is-one-fifth`"
        >
          <b-autocomplete
            v-model="documentTypeSelected"
            :data="filterTypes"
            data-cy="type-input"
            autocomplete
            field="type"
            name="id"
            :disabled="
              file.is_doe_document_model ||
                file.to_publish ||
                hasSignature ||
                file.non_mutable
            "
            :placeholder="General.SELECT_DOCUMENT_TYPE"
            keepFirst
            role="combobox"
            @typing="getFilterTypes"
            @select="option => selectType(option)"
            @input="value => !value && selectType()"
          >
            <template slot-scope="props">
              <div
                class="is-flex is-justify-content-space-between custom-bottom-border"
              >
                <div class="pre-wrap">
                  {{ props.option.type }}
                </div>
                <b-tag
                  v-if="props.option.to_publish"
                  rounded
                  :type="isContrast ? 'is-white' : 'is-blue-oil'"
                >
                  <span>{{ General.DOE }}</span>
                </b-tag>
              </div>
            </template>
          </b-autocomplete>
        </b-field>

        <b-field
          v-if="isEditable && hasPermissionPublishDOE"
          class="column is-one-fifth mb-0 py-0"
        >
          <b-tooltip
            type="is-blue-oil"
            position="is-right"
            multilined
            size="is-large"
            :active="disableDoeSwitch"
            role="tooltip"
          >
            <template v-slot:content>
              <h6 class="has-text-weight-semibold is-flex">
                {{ General.ALERT }}!
              </h6>
              <p class="has-text-left">
                {{ getPublishDoeAlert }}
              </p>
            </template>
            <b-switch
              :key="publishSwitch"
              v-model="publishSwitch"
              leftLabel
              :disabled="disableDoeSwitch"
              role="switch"
              @input="value => setToPublish(value)"
              ><span class="single-line">{{ General.DOE_PUBLISH }}</span>
            </b-switch>
          </b-tooltip>
        </b-field>

        <div class="column is-full-mobile py-0">
          <div class="columns is-vcentered is-multiline">
            <b-field
              v-if="file.to_publish && !isEditable"
              class="column is-4 mb-0"
            >
              <b-tag
                rounded
                :type="isContrast ? 'is-orange' : 'is-blue-oil'"
                class="custom-tag-mobile"
              >
                {{ General.DOE_PUBLISH }}
              </b-tag>
            </b-field>
            <div class="column columns is-mobile">
              <div class="column tooltip-container">
                <b-tooltip
                  v-if="
                    mandatorySignatures &&
                      mandatorySignatures.length &&
                      hasPermissionMandatorySignature
                  "
                  :type="isContrast ? 'is-black' : 'is-white'"
                  multilined
                  :always="showTooltip[DOC_SIGN.MANDATORY]"
                  class="list-sign"
                  :size="isMobile ? tooltipMobileSize : 'is-large'"
                  :position="isMobile ? tooltipOpenMobile : 'is-top'"
                  role="tooltip"
                >
                  <div
                    @mouseover="setShowTooltip(DOC_SIGN.MANDATORY, true)"
                    @mousemove="setShowTooltip(DOC_SIGN.MANDATORY, false)"
                  >
                    <b-icon
                      type="is-blue-oil"
                      :icon="hasUnsignedSignature ? 'circle' : 'pencil-circle'"
                      size="is-small"
                      class="is-flex"
                    />
                  </div>
                  <template v-slot:content>
                    <section
                      class="container-new"
                      @mouseout="setShowTooltip(DOC_SIGN.MANDATORY, false)"
                      @mousemove="setShowTooltip(DOC_SIGN.MANDATORY, true)"
                    >
                      <span class="has-text-color-7 has-text-weight-semibold">{{
                        General.MANDATORY_SIGNATURE
                      }}</span>
                      <div
                        v-for="(signature, index) in mandatorySignatures.slice(
                          0,
                          4
                        )"
                        :key="signature.id"
                        class="has-text-color-7 is-justify-content-space-between"
                        :class="
                          signature.is_signed.toLowerCase() || 'mandatory'
                        "
                      >
                        <div>
                          <br />
                          <div class="spacing-mr-5"><i class="ball"></i></div>
                          <span class="flex-1 has-text-left">
                            {{
                              `${signature.user_name} ${General
                                .ASSIGN_STATE_MANDATORY[signature.is_signed] ||
                                ""}${
                                mandatorySignatures.length > 4 && index == 3
                                  ? " " + General.TEXTS.AND_OTHERS
                                  : ""
                              }${
                                index ==
                                mandatorySignatures.slice(0, 4).length - 1
                                  ? "."
                                  : ""
                              }`
                            }}
                          </span>
                        </div>
                        <div>
                          <b-tooltip
                            v-if="signature.is_signed !== 'assigned'"
                            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                            position="is-top"
                            multilined
                            size="is-small"
                            :label="General.DELETE"
                            role="tooltip"
                          >
                            <span
                              class="is-clickable"
                              @click="removeMandatory(signature)"
                            >
                              <b-icon
                                icon="delete"
                                :type="
                                  isContrast ? 'is-white-dark' : 'is-blue-oil'
                                "
                              />
                            </span>
                          </b-tooltip>
                          <b-tooltip
                            v-if="!signature.is_signed && !isEditable"
                            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                            position="is-top"
                            multilined
                            size="is-small"
                            :label="General.REQUEST_SUBSCRIPTION"
                            role="tooltip"
                          >
                            <span
                              class="is-clickable"
                              @click="requestSignature(signature)"
                            >
                              <b-icon
                                icon="pen-plus"
                                :type="
                                  isContrast ? 'is-white-dark' : 'is-blue-oil'
                                "
                              />
                            </span>
                          </b-tooltip>
                        </div>
                      </div>
                    </section>
                  </template>
                </b-tooltip>

                <b-tooltip
                  v-if="processFileSignCount(DOC_SIGN.SIGNED)"
                  :type="isContrast ? 'is-black' : 'is-white'"
                  multilined
                  :always="showTooltip[DOC_SIGN.SIGNED]"
                  class="list-sign signed-tooltip"
                  :size="isMobile ? tooltipMobileSize : 'is-large'"
                  :position="isMobile ? tooltipOpenMobile : 'is-top'"
                  role="tooltip"
                >
                  <div
                    @mouseover="setShowTooltip(DOC_SIGN.SIGNED, true)"
                    @mousemove="setShowTooltip(DOC_SIGN.SIGNED, false)"
                  >
                    <b-icon
                      type="is-primary"
                      icon="checkbox-marked-circle"
                      size="is-small"
                      class="is-flex"
                    />
                  </div>
                  <template v-slot:content>
                    <section
                      class="container-new"
                      @mouseout="setShowTooltip(DOC_SIGN.SIGNED, false)"
                      @mousemove="setShowTooltip(DOC_SIGN.SIGNED, true)"
                    >
                      <span class="has-text-color-7 has-text-weight-semibold">{{
                        General.SIGNATURES_MADE
                      }}</span>
                      <div
                        v-for="(item, index) in processFileSign(
                          DOC_SIGN.SIGNED
                        )"
                        :key="item.id"
                        :class="item.status.toLowerCase()"
                        class="has-text-color-7"
                      >
                        <br />
                        <div>
                          <div class="spacing-mr-5"><i class="ball"></i></div>
                        </div>
                        <span class="flex-1 has-text-left">
                          {{
                            `${item.user_name} (${capacityFormatted(
                              item.capacity
                            )})${
                              processFileSignCount(DOC_SIGN.SIGNED) > 4 &&
                              index == 3
                                ? " " + General.TEXTS.AND_OTHERS
                                : ""
                            }${
                              index ==
                              processFileSign(DOC_SIGN.SIGNED).length - 1
                                ? "."
                                : ""
                            }`
                          }}</span
                        >
                      </div>
                      <div class="action-signature is-justify-content-flex-end">
                        <span
                          class="has-text-color-7 has-text-weight-semibold mt-5 is-clickable"
                          @click="removeSignatureModal()"
                          >{{ General.REMOVE_ALL }}</span
                        >
                      </div>
                    </section>
                  </template>
                </b-tooltip>

                <b-tooltip
                  v-if="processFileSignCount(DOC_SIGN.PENDING)"
                  :type="isContrast ? 'is-black' : 'is-white'"
                  multilined
                  :always="showTooltip[DOC_SIGN.PENDING]"
                  class="list-sign pending-tooltip"
                  :size="isMobile ? tooltipMobileSize : 'is-large'"
                  :position="isMobile ? tooltipOpenMobile : 'is-top'"
                  role="tooltip"
                >
                  <div
                    @mouseover="setShowTooltip(DOC_SIGN.PENDING, true)"
                    @mousemove="setShowTooltip(DOC_SIGN.PENDING, false)"
                  >
                    <b-icon
                      type="is-pending"
                      icon="alert-circle"
                      size="is-small"
                      class="is-flex"
                    />
                  </div>
                  <template v-slot:content>
                    <section
                      class="container-new"
                      @mouseout="setShowTooltip(DOC_SIGN.PENDING, false)"
                      @mousemove="setShowTooltip(DOC_SIGN.PENDING, true)"
                    >
                      <span class="has-text-color-7 has-text-weight-semibold">{{
                        General.SIGNATURES_PENDING
                      }}</span>
                      <div
                        v-for="(signature, index) in processFileSign(
                          DOC_SIGN.PENDING
                        )"
                        :key="signature.id"
                        :class="signature.status.toLowerCase()"
                        class="has-text-color-7"
                      >
                        <br />
                        <div>
                          <div class="spacing-mr-5"><i class="ball"></i></div>
                        </div>
                        <span class="flex-1 has-text-left">
                          {{
                            `${signature.user_name} (${capacityFormatted(
                              signature.capacity
                            )})${
                              processFileSignCount(DOC_SIGN.PENDING) > 4 &&
                              index == 3
                                ? " " + General.TEXTS.AND_OTHERS
                                : ""
                            }${
                              index ==
                              processFileSign(DOC_SIGN.PENDING).length - 1
                                ? "."
                                : ""
                            }`
                          }}
                        </span>
                        <span
                          class="is-clickable"
                          @click="cancelSignatureModal(signature)"
                        >
                          <b-icon
                            icon="delete"
                            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                          />
                        </span>
                      </div>
                      <div class="action-signature">
                        <span
                          class="has-text-color-7 has-text-weight-semibold mt-5 is-clickable"
                          @click="cancelSignatureModal(false, DOC_SIGN.PENDING)"
                          >{{ General.REMOVE_ALL }}</span
                        >
                      </div>
                    </section>
                  </template>
                </b-tooltip>

                <b-tooltip
                  v-if="processFileSignCount(DOC_SIGN.REFUSED)"
                  :type="isContrast ? 'is-black' : 'is-white'"
                  multilined
                  :always="showTooltip[DOC_SIGN.REFUSED]"
                  class="list-sign refused-tooltip"
                  :size="isMobile ? tooltipMobileSize : 'is-large'"
                  :position="isMobile ? tooltipOpenMobile : 'is-top'"
                  role="tooltip"
                >
                  <div
                    @mouseover="setShowTooltip(DOC_SIGN.REFUSED, true)"
                    @mousemove="setShowTooltip(DOC_SIGN.REFUSED, false)"
                  >
                    <b-icon
                      type="is-refused"
                      icon="close-circle"
                      size="is-small"
                      class="is-flex"
                    />
                  </div>
                  <template v-slot:content>
                    <section
                      class="container-new"
                      @mouseout="setShowTooltip(DOC_SIGN.REFUSED, false)"
                      @mousemove="setShowTooltip(DOC_SIGN.REFUSED, true)"
                    >
                      <span class="has-text-color-7 has-text-weight-semibold">{{
                        General.SIGNATURES_REFUSED
                      }}</span>
                      <div
                        v-for="(item, index) in processFileSign(
                          DOC_SIGN.REFUSED
                        )"
                        :key="item.id"
                        :class="item.status.toLowerCase()"
                        class="has-text-color-7"
                      >
                        <br />
                        <div>
                          <div class="spacing-mr-5"><i class="ball"></i></div>
                        </div>
                        {{
                          `${item.user_name} (${capacityFormatted(
                            item.capacity
                          )})${
                            processFileSignCount(DOC_SIGN.REFUSED) > 4 &&
                            index == 3
                              ? " " + General.TEXTS.AND_OTHERS
                              : ""
                          }${
                            index ==
                            processFileSign(DOC_SIGN.REFUSED).length - 1
                              ? "."
                              : ""
                          }`
                        }}
                      </div>
                      <div class="action-signature has-text-color-7">
                        <span
                          class="has-text-weight-semibold mt-5 is-clickable"
                          @click="cancelSignatureModal(false, DOC_SIGN.REFUSED)"
                          >{{ General.DELETE }}</span
                        >
                        <span
                          class="has-text-weight-semibold mt-5 is-clickable"
                          @click="resendSignatureModal(false, DOC_SIGN.REFUSED)"
                          >{{ General.REQUEST_AGAIN }}</span
                        >
                      </div>
                    </section>
                  </template>
                </b-tooltip>
              </div>
              <div class="column actions-row">
                <ActionsGrouper
                  :actions="actionList()"
                  v-bind="isMobile ? { defaultDotsMaxActions: 2 } : {}"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";

import ModalDocSignature from "@/views/App/ModalDocSignature";
import ModalDocMandatorySignature from "@/views/App/ModalDocMandatorySignature";
import ModalSignDocument from "@/views/App/ModalSignDocument";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { App, General } from "@/utils/constants";
import { downloadWithURL } from "@/utils/fileUtility";
import ModalWarning from "@/views/App/ModalWarning.vue";
import {
  USER_PERMISSIONS as userPermissions,
  DOC_SIGNATURES_STATUS as DOC_SIGN,
  SIZES as sizes,
  ATTACHEMENT_MAX_COUNT,
  MAX_SIGN_SIZE
} from "@/utils/enumerate";
import { showErrorMessage } from "@/utils/messageUtility";

export default {
  name: "DisplayAttachedDoc",
  props: {
    process: {
      type: Object,
      default: () => {}
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    initialDoc: {
      type: Boolean,
      default: false
    },
    onEdit: {
      type: Function,
      default: () => null
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    isJoint: {
      type: Boolean,
      default: false
    },
    showSignAlertModal: {
      type: Boolean,
      default: false
    },
    isSignatureDone: {
      type: Boolean,
      default: false
    },
    file: {
      type: Object,
      required: true
    },
    showPDF: {
      type: Boolean,
      default: false
    },
    tooltipOpenMobile: {
      type: String,
      default: "is-right"
    },
    disableDoeSizeLimit: {
      type: Boolean,
      default: false
    },
    removeFile: {
      type: Function,
      default: () => null
    },
    isRevertNoEffect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      App,
      General,
      documentTypeSelected: null,
      filterTypes: [],
      documentsSelectedSignRequested: [],
      DOC_SIGN,
      showTooltip: {
        REFUSED: false,
        PENDING: false,
        SIGNED: false,
        MANDATORY: false
      },
      isLoading: false,
      publishSwitch: this.file.to_publish,
      type_id: this.file.document_type,
      fileName: ""
    };
  },
  computed: {
    ...mapGetters({
      fileTypes: "getFileTypesByEditor",
      getFileTypeById: "getFileTypeById",
      newProcess: "getNewProcess",
      getDispatch: "getDispatch",
      user: "getCurrentUser",
      hasPermission: "hasPermission",
      isContrast: "isContrast",
      isMobile: "isMobile",
      getCapacityFormatted: "getCapacityFormatted",
      getPublishExtension: "getPublishExtensionList",
      mobileSize: "mobileSize"
    }),

    hasSignature() {
      return this.file.sign_history?.some(history => !history.only_historical);
    },

    hasPermissionPublishDOE() {
      return this.hasPermission(userPermissions["PUBLISH_DOE"]);
    },

    hasDOETypeValid() {
      return this.getFileTypeById(this.type_id || this.file.document_type)
        .to_publish;
    },

    doeAttachmentExtension() {
      const extensionList = this.getPublishExtension || [];
      if (!extensionList.length) return false;
      return extensionList.some(({ name }) => this.file.file?.endsWith(name));
    },

    hasPermissionMandatorySignature() {
      return this.hasPermissionPublishDOE || this.hasPermissionAuthorizeDOE;
    },

    hasPermissionAuthorizeDOE() {
      return this.hasPermission(userPermissions["AUTHORIZE_DOE"]);
    },

    hasGdocIdOrDoeExtensionXOrJson() {
      return (
        this.file.gdocs_id ||
        this.doeAttachmentExtension ||
        this.file.is_doe_document_model
      );
    },

    disableDoeSwitch() {
      return (
        !this.hasDOETypeValid ||
        this.disableDoeSizeLimit ||
        !this.hasGdocIdOrDoeExtensionXOrJson
      );
    },

    isAutomaticProcessing() {
      if (this.newProcess?.id === this.file.process_id) {
        return (
          this.newProcess.automatic_processing &&
          this.newProcess.user_id === this.user.id
        );
      } else if (this.getDispatch?.id === this.file.dispatch_id) {
        return (
          this.getDispatch.automatic_processing &&
          this.getDispatch.user_id === this.user.id
        );
      } else {
        return false;
      }
    },

    messageDeleteAttachment() {
      if (this.file.to_publish && this.isEditable)
        return this.General.MESSAGES.DO_YOU_WANT_DELETE_REQUEST_DOE_EDITABLE;
      if (this.file.to_publish)
        return this.General.MESSAGES.DO_YOU_WANT_DELETE_REQUEST_DOE;
      return this.General.MESSAGES.DO_YOU_WANT_DELETE_REQUEST;
    },

    mandatorySignatures() {
      return this.file.doe_obrigatory_signature;
    },

    hasFile() {
      return Boolean(this.file.file);
    },

    showView() {
      const hasHistory = Boolean(this.file.sign_history?.length);
      const hasSignature =
        hasHistory &&
        this.file.sign_history.some(history => !history.only_historical);
      const hasPermission = this.hasPermission(userPermissions["PROTOCOLLER"]);
      return (
        (!this.file.gdocs_id && !this.file.editable) ||
        hasSignature ||
        (this.file.primary && hasPermission)
      );
    },

    htmlSource() {
      return Boolean(this.file.html_source);
    },

    getDocumentType() {
      if (this.file.document_type)
        return this.$store.getters.getFileTypeById(this.file.document_type)
          .type;
      return null;
    },

    getPublishDoeAlert() {
      if (!this.disableDoeSwitch) return "";
      if (!this.doeAttachmentExtension)
        return this.General.TEXTS.DOE_ALERT_WRONG_EXTENSION;
      if (this.disableDoeSizeLimit)
        return this.General.TEXTS.DOE_ALERT_MAX_SIZE;
      if (!this.hasDOETypeValid) return this.General.TEXTS.DOE_ALERT_CREATE;
      return "";
    },

    hasUnsignedSignature() {
      return this.file?.doe_obrigatory_signature?.some(
        signature => signature.is_signed !== "assigned"
      );
    },

    hasCurrentUserValidSignature() {
      return this.file.sign_history?.length
        ? this.file.sign_history.some(
            signature =>
              signature.user_id === this.user.id &&
              signature.only_historical === false
          )
        : false;
    },

    tooltipMobileSize() {
      if (this.mobileSize === sizes.IS_SMALL) return sizes.IS_SMALL;
      else return sizes.IS_MEDIUM;
    },

    showInputCounter() {
      return this.isEditable
        ? this.fileName.length == 70
        : this.fileName.length == 100;
    },
    editMessage() {
      return this.file.dispatch_id
        ? this.General.MESSAGES.WARNING_DISPATCH_DOCUMENT_WITH_SIGN_DONE
        : this.General.MESSAGES.WARNING_DOCUMENT_WITH_SIGN_DONE;
    }
  },
  watch: {
    file: {
      handler() {
        this.publishSwitch = this.file.to_publish;
        this.type_id = this.file.document_type;
      }
    },
    getDocumentType(type) {
      this.documentTypeSelected = type;
    }
  },
  beforeMount() {
    this.getFilterTypes("");
    this.documentTypeSelected = this.getDocumentType;
    this.fileName = this.file?.attachment_name || "";
  },
  methods: {
    ...mapActions({
      deleteProcessFileRequest: "deleteProcessFileRequest",
      processAutoDraftRequest: "processAutoDraftRequest",
      postSubscriberRequest: "postSubscriberRequest",
      postExternalSubscriberRequest: "postExternalSubscriberRequest",
      cancelSignatureFile: "cancelSignatureFileRequest",
      setAutoProcessing: "setProcessAutoProcessing",
      setAutoProcessingDispatch: "setDispatchAutoProcessing",
      processToPublishRequest: "processToPublishRequest",
      deleteObrigatorySignature: "deleteObrigatorySignature",
      deleteSignatures: "deleteSignatureFile",
      requestFile: "requestDataProcessFile",
      requestProcessFiles: "processFilesFilteredRequest",
      getJointFile: "getJointFile",
      updateProcessFile: "updateProcessFile",
      getFileSizeRequest: "getFileSizeRequest"
    }),
    ...mapMutations(["setCurrentProcessFileList"]),

    getFilterTypes(text) {
      this.filterTypes = this.fileTypes(1).filter(option =>
        option.type.toLowerCase().includes(text.toLowerCase())
      );
    },

    async publishDocument(toPublish) {
      this.isLoading = true;
      const result = await this.processToPublishRequest({
        toPublish,
        processId: this.file.process_id,
        processFileId: this.file.id,
        dispatchId: this.file.dispatch_id,
        keepReference: false,
        nup: this.process.nup
      });
      this.isLoading = false;
      return result;
    },

    async setToPublish(toPublish) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: toPublish
            ? `${General.MESSAGES.DOCUMENT_WILL_BE_PUBLISHED_PROCESS}`
            : `${General.MESSAGES.DOCUMENT_WILL_NOT_BE_PUBLISHED_AND_DELETED}`,
          confirm: async () => {
            const successPublish = await this.publishDocument(toPublish);
            this.updateProcessFile({
              list: [
                {
                  ...this.file,
                  to_publish: successPublish ? toPublish : !toPublish
                }
              ]
            });
            successPublish &&
              toPublish &&
              this.showMandatorySignatureModal(successPublish);
          },
          deny: () =>
            this.updateProcessFile({
              list: [{ ...this.file, to_publish: !toPublish }]
            }),
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true,
        onCancel: () =>
          this.updateProcessFile({
            list: [{ ...this.file, to_publish: !toPublish }]
          })
      });
    },

    async selectType(option) {
      if (option) {
        this.isLoading = true;
        this.type_id = option.id;
        this.documentTypeSelected = option.type;
        await this.onEdit(
          this.file.id,
          "document_type",
          option.id,
          this.isEditable
        );
        this.isLoading = false;
      }
    },

    confirmOverwriteEdit() {
      if (this.isSignatureDone) {
        this.snackToast.open({
          message: this.editMessage,
          type: "is-warning",
          duration: 8000
        });
        return;
      }
      if (this.file.status)
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: `${this.General.MESSAGES.EDIT_SIGNED_DOCUMENT}`,
            confirm: () => this.editDocument(),
            buttons: [
              { label: General.YES, isConfirm: false, action: true },
              { label: General.NO, isConfirm: true, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      else this.editDocument();
    },

    editDocument() {
      const template = this.getFileTypeById(this.file.id);
      const templateId = template?.id || this.file.document_type;
      const path = `/app/documento-editor/${this.file.process_id}`;

      if (this.file.document_json?.length)
        this.$router.push({
          path: `doe-formulario`,
          query: {
            processId: this.file.process_id,
            fileId: this.file.id
          }
        });
      else
        this.$router.push({
          path,
          query: {
            template: templateId,
            fileId: this.file.id,
            dispatchId: this.file.dispatch_id,
            isPrimary: this.file.primary
          }
        });
    },

    async downloadFile() {
      this.isLoading = true;
      const result = await this.requestFile({
        fileIds: [{ process_file_id: this.file.id }],
        processId: this.process.id
      });
      this.isLoading = false;
      if (result) {
        const [{ url_file, attachment_name }] = result;
        downloadWithURL(
          url_file,
          attachment_name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\W/g, "_")
        );
      }
    },

    showModalDocSignature() {
      this.setCurrentProcessFileList([this.file]);
      this.$buefy.modal.open({
        parent: this,
        component: ModalDocSignature,
        props: {
          updateFiles: this.updateProcessFiles,
          dispatchId: this.file.dispatch_id,
          processId: this.file.process_id,
          joinId: this.file.document_join_id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async updateProcessFiles() {
      if (this.isJoint && this.file.document_join_id) {
        await this.getJointFile({
          jointId: this.file.document_join_id,
          page: 1,
          paginator_count: ATTACHEMENT_MAX_COUNT,
          files_id: JSON.stringify([this.file.id])
        });
      } else if (this.initialDoc) {
        await this.requestProcessFiles({
          processId: this.file.process_id,
          dispatch_id: this.file.dispatch_id,
          is_primary: true
        });
      } else {
        await this.requestProcessFiles({
          files_id: [this.file.id],
          processId: this.file.process_id,
          dispatch_id: this.file.dispatch_id,
          isArchived: this.isArchived
        });
      }
    },

    handleDocumentSelectedSign() {
      this.$store.commit("setListSelectedDocuments", {
        documents: [
          {
            process_file: {
              attachment_name: this.file.attachment_name,
              id: this.file.id,
              document_type: { type: this.getDocumentType },
              process: {
                nup: this.process.nup || General.TEXTS.NUP_NOT_GENERATED
              },
              process_id: this.file.process_id,
              dispatch_id: this.file.dispatch_id
            }
          }
        ],
        capacity_id: this.user.capacity.capacity_id
      });
    },

    isLargeFile(fileSize) {
      return fileSize > MAX_SIGN_SIZE;
    },

    async sizeCanSign(isSignRequest) {
      if (!this.file.primary) {
        let fileSize = this.file.filesize;
        this.isLoading = true;
        if (!fileSize) fileSize = await this.getFileSizeRequest(this.file.id);
        this.isLoading = false;

        if (this.isLargeFile(fileSize)) {
          const message = isSignRequest
            ? this.General.MESSAGES.ALERT_REQUEST_SIGNATURE_OVERSIZED_FILE
            : this.General.MESSAGES.ALERT_SIGN_OVERSIZED_FILE;
          showErrorMessage(message, false, "is-danger");
          return false;
        }
      }
      return true;
    },

    showModalSignatureAlert(file, isSignRequest) {
      let messageType;

      if (file.dispatch_id) {
        messageType = this.App.ProcessDetailsScreen.ORDERS.toLowerCase();
      } else if (file.document_join_id) {
        messageType = this.General.MERGE.toLowerCase();
      } else {
        messageType = this.App.ProcessOpeningScreen.SOLICITATION.toLowerCase();
      }

      const message = this.General.MESSAGES.DO_YOU_WANT_SIGNED_DOC.replaceAll(
        "TYPE_DOC",
        messageType
      );

      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message,
          confirm: async () => {
            isSignRequest
              ? this.showModalDocSignature()
              : this.showModalSignDocument();
          },
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showAlertModalSignDocument: debounce(async function(isSignRequest = false) {
      const canSign = await this.sizeCanSign(isSignRequest);
      if (!canSign) return;

      const withoutSubject = !(
        this.process?.subject || this.process?.subject_name
      );

      if (this.process && withoutSubject) {
        showErrorMessage(this.General.MESSAGES.INSERT_SUBJECT_ON_PROCESS);
      } else if (isSignRequest && this.showSignAlertModal) {
        this.showModalSignatureAlert(this.file, isSignRequest);
      } else {
        isSignRequest
          ? this.showModalDocSignature()
          : this.showModalSignDocument();
      }
    }, 250),

    showModalSignDocument() {
      this.handleDocumentSelectedSign();

      this.$buefy.modal.open({
        parent: this,
        component: ModalSignDocument,
        props: {
          onClose: this.updateProcessFiles,
          isArchived: this.isArchived,
          isRevertNoEffect: this.isRevertNoEffect
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async deleteFile() {
      if (this.isSignatureDone) {
        this.snackToast.open({
          message: this.editMessage,
          type: "is-warning",
          duration: 8000
        });
        return;
      }
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: this.messageDeleteAttachment,
          confirm: async () => {
            const success = await this.deleteProcessFileRequest({
              file: this.file,
              isArchived: this.isArchived,
              isRevertNoEffect: this.isRevertNoEffect
            });
            if (success) this.removeFile();
            if (this.isAutomaticProcessing) {
              if (this.file.dispatch_id)
                await this.setAutoProcessingDispatch({
                  dispatchId: this.file.dispatch_id,
                  automatic: false
                });
              else if (this.file.process_id)
                await this.setAutoProcessing({
                  processId: this.file.process_id,
                  automatic: false
                });
            }
          },
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    checkFieldName({ target }) {
      if (target.value)
        this.onEdit(
          this.file.id,
          "attachment_name",
          target.value,
          this.isEditable
        );
      else {
        this.snackToast.open({
          message: `${this.General.MESSAGES.ATTACHMENT_NAME}.`,
          type: "is-danger",
          duration: 8000
        });
      }
    },

    signatureHistoryType(type) {
      return this.file.sign_history?.length
        ? this.file.sign_history.filter(
            signature => signature.status === type && !signature.only_historical
          )
        : [];
    },

    processFileSignCount(type) {
      return this.signatureHistoryType(type).length;
    },

    processFileSign(type) {
      return this.signatureHistoryType(type).slice(0, 4);
    },

    setShowTooltip(type, state) {
      this.showTooltip[type] = state;
    },

    async cancelRequest() {
      this.isLoading = true;
      const list_signature_cancel = this.documentsSelectedSignRequested.map(
        signature => ({
          signature_file_id: signature.id
        })
      );
      await this.cancelSignatureFile(list_signature_cancel);
      this.requestProcessFiles({
        files_id: [this.file.id],
        processId: this.file.process_id,
        isArchived: this.isArchived
      });
      if (this.isAutomaticProcessing) {
        if (this.file.dispatch_id)
          await this.setAutoProcessingDispatch({
            dispatchId: this.file.dispatch_id,
            automatic: false
          });
        else if (this.file.process_id)
          await this.setAutoProcessing({
            processId: this.file.process_id,
            automatic: false
          });
      }

      this.isLoading = false;
    },

    async resendRequest() {
      this.isLoading = true;
      const internalSignatureList = this.documentsSelectedSignRequested
        .filter(sub => sub.user_id)
        .map((sub, index) => {
          return {
            user_id: sub.user_id,
            capacity_id: sub.capacity,
            process_file_id: this.file.id,
            order: index
          };
        });

      const externalSignatureList = this.documentsSelectedSignRequested
        .filter(sub => !sub.user_id)
        .map(sub => {
          return {
            email: sub.external_signature_email,
            name: sub.user_name,
            capacity_id: sub.capacity,
            process_file_id: this.file.id,
            cpf_cnpj: sub.user_cpf.replace(/\D/g, ""),
            id: sub.id
          };
        });
      if (internalSignatureList.length)
        await this.postSubscriberRequest({
          subscribers: [internalSignatureList],
          processId: this.process.id,
          hasOrder: false,
          joinId: this.file.document_join_id
        });
      if (externalSignatureList.length)
        await this.postExternalSubscriberRequest({
          subscriber: externalSignatureList,
          processId: this.process.id
        });
      await this.requestProcessFiles({
        files_id: [this.file.id],
        processId: this.file.process_id,
        isArchived: this.isArchived
      });
      this.isLoading = false;
    },

    async cancelSignatureModal(signature = null, type = null) {
      if (signature?.id) this.documentsSelectedSignRequested = [signature];
      else if (type)
        this.documentsSelectedSignRequested = this.signatureHistoryType(type);

      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${this.General.TEXTS.CONFIRM_CANCEL_REQUEST}?`,
          confirm: () => this.cancelRequest(),
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    removeSignatureModal() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${this.General.TEXTS.DELETE_SIGNED_REQUEST}?`,
          confirm: async () => {
            this.isLoading = true;
            await this.deleteSignatures({
              processFileId: this.file.id,
              processId: this.file.dispatch_id ? null : this.file.process_id
            });
            await this.updateProcessFiles();
            this.isLoading = false;
          },
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async resendSignatureModal(signature = null, type = null) {
      if (signature?.id) this.documentsSelectedSignRequested = [signature];
      else if (type)
        this.documentsSelectedSignRequested = this.signatureHistoryType(type);

      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${this.General.MSG_RESEND_SIGNATURE}?`,
          confirm: () => this.resendRequest(),
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showMandatorySignatureModal(processFileId) {
      this.$store.commit("setCurrentProcessFileId", this.file.id);
      this.$buefy.modal.open({
        parent: this,
        component: ModalDocMandatorySignature,
        props: {
          updateFiles: () => {
            this.requestProcessFiles({
              files_id: [processFileId],
              dispatchId: this.file.dispatch_id,
              processId: this.file.process_id
            });
            this.updateProcessFiles();
          },
          processId: this.file.process_id,
          joinId: this.file.document_join_id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async removeMandatory(signature) {
      this.isLoading = true;
      if (signature?.id) await this.deleteObrigatorySignature(signature.id);
      await this.updateProcessFiles();
      this.isLoading = false;
    },

    async requestSignature(signatureRef) {
      this.isLoading = true;
      if (signatureRef?.id) {
        const subscribers = [
          [
            {
              user_id: signatureRef.user_id,
              capacity_id: signatureRef.capacity.id,
              process_file_id: this.file.id,
              order: 0
            }
          ]
        ];
        await this.postSubscriberRequest({
          subscribers,
          hasOrder: false,
          dispatchId: this.file.dispatch_id,
          processId: this.file.process_id,
          joinId: this.file.document_join_id
        });
        await this.requestProcessFiles({
          files_id: [this.file.id],
          processId: this.file.process_id,
          isArchived: this.isArchived
        });
      }
      this.isLoading = false;
    },

    actionList() {
      return [
        {
          label: this.General.INFO_MANDATORY_SIGNATURE,
          icon: "pencil-circle",
          permission: this.hasPermissionMandatorySignature,
          validated: this.hasGdocIdOrDoeExtensionXOrJson && this.publishSwitch,
          callback: () => this.showMandatorySignatureModal()
        },
        {
          label: this.General.TO_VIEW,
          icon: "magnify-plus",
          validated: this.showView && !this.isArchived,
          link:
            !this.showPDF &&
            `/app/documento/${this.file.id}?processId=${this.file.process_id}`,
          callback: () => this.downloadFile()
        },
        {
          label: this.General.EDIT,
          icon: "square-edit-outline",
          validated:
            (((this.file.primary &&
              !this.hasPermission(userPermissions["PROTOCOLLER"])) ||
              this.file.gdocs_id) &&
              !this.file.to_publish) ||
            (this.file.document_json?.length && !this.file.to_publish),
          callback: () => this.confirmOverwriteEdit()
        },
        {
          label: this.General.SIGN,
          icon: "pen",
          validated: !this.isEditable && !this.hasCurrentUserValidSignature,
          dataCY: "sing-document",
          callback: () => this.showAlertModalSignDocument()
        },
        {
          label: this.General.REQUEST_SUBSCRIPTION,
          icon: "pen-plus",
          validated: !this.isEditable && !this.isArchived,
          callback: () => this.showAlertModalSignDocument(true),
          dataCY: "SIGN_BUTTON"
        },
        {
          label: this.General.DOWNLOAD,
          icon: "download",
          validated: this.isEditable || this.hasFile,
          callback: () => this.downloadFile()
        },
        {
          label: this.General.DELETE,
          icon: "delete",
          validated: !this.file.non_mutable,
          callback: () => this.deleteFile(),
          dataCY: "REMOVE_FILE"
        }
      ];
    },
    capacityFormatted(capacity_id) {
      return capacity_id
        ? this.getCapacityFormatted(capacity_id)
        : General.EXTERNAL_USER;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.custom-ph-8 {
  padding: 0 8px;
}

.pre-wrap {
  white-space: pre-wrap;
  border: none;
}

.actions-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;

  @media screen and (max-width: 768px) {
    flex-wrap: wrap;
  }
}

.list-sign div {
  display: flex;
  align-items: center;
}

.list-sign div.pending > div > div > .ball,
.list-sign div.signed > div > div > .ball,
.list-sign div.refused > div > div > .ball,
.list-sign div.mandatory > div > div > .ball,
.list-sign div.assigned > div > div > .ball,
.circle {
  border-radius: 50%;
  height: 8px;
  width: 8px;
  box-shadow: 0px 0px 8px 1px #f2f2f2;
  margin-right: 5px;
}

.list-sign div.pending > div > div > .ball,
.circle.pending {
  background-color: $pending-color;
}

.list-sign div.signed > div > div > .ball,
.circle.signed {
  background-color: $primary;
}

.list-sign div.refused > div > div > .ball,
.circle.refused {
  background-color: $refused-color;
}

.list-sign div.mandatory > div > div > .ball,
.circle.mandatory {
  background-color: $blue-oil;
}

.list-sign div.assigned > div > div > .ball,
.circle.assigned {
  background-color: $primary;
}

.circle {
  height: 12px;
  width: 12px;
  cursor: pointer;
}

.list-sign .tooltip-content {
  background-color: red !important;
  color: blue !important;
}

.container-new {
  text-align: left;
  color: #000;
  font-weight: 300;
}

.width-30 {
  min-width: 30px;
  min-height: 15px;
}

.tooltip-container {
  display: flex;
  justify-content: flex-start;
  margin: auto;
  gap: 10px;
}

.action-signature {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid $border-color;
  margin-top: 5px;
  color: $blue-oil;
}

.row-file {
  width: 100%;
}

@media screen and (min-width: 833px) {
  .custom-tablet-mx {
    margin-left: 25px;
    margin-right: 20px;
  }
  .custom-tablet-mb {
    margin-bottom: 0;
  }
}

@media screen and (max-width: $tablet) {
  .custom-tag-mobile {
    width: 100%;
  }

  .single-line {
    white-space: nowrap;
  }

  .no-margin-x {
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
